import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import logoImage from "@/assets/zavvi-logo.png";

const TermosDeUso = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="py-4 px-4 border-b border-border bg-card/50">
        <div className="container mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={logoImage} alt="Zavvi.ai" className="h-8 object-contain" />
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={16} />
            Voltar ao site
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-2">
          Termos e Condições de Uso
        </h1>
        <p className="text-muted-foreground text-sm mb-8">
          Data de disponibilização: 14/01/2026 | Última atualização: 14/01/2026
        </p>

        <div className="prose prose-invert max-w-none space-y-8 text-body-text">
          <p className="text-sm leading-relaxed">
            Estes Termos e Condições de Uso ("Termos") regulam a relação comercial e a prestação de
            serviços entre a empresa <strong>GF SERVICOS ONLINE</strong>, pessoa jurídica de direito
            privado, inscrita no CNPJ sob o nº 55.287.952/0001-2, doravante denominada "ZAVVI" ou "PRESTADORA", e a pessoa física ou jurídica que contrata o serviço, doravante denominada
            "CLIENTE" ou "ASSINANTE".
          </p>
          
          <p className="text-sm leading-relaxed">
            O objeto deste instrumento é o regramento da utilização do software de gestão e automação de
            atendimentos denominado "PLATAFORMA ZAVVI", fornecido na modalidade SaaS (Software as a Service).
          </p>

          <div className="bg-electric-purple/10 border border-electric-purple/20 rounded-lg p-4">
            <p className="text-sm font-semibold text-foreground">
              AO CONTRATAR E UTILIZAR A PLATAFORMA ZAVVI, O CLIENTE DECLARA TER LIDO, COMPREENDIDO E ACEITO INTEGRALMENTE ESTES TERMOS.
            </p>
          </div>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">ÍNDICE</h2>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Conceitos Importantes</li>
              <li>Natureza e Eficácia dos Termos</li>
              <li>Requisitos Técnicos e Condições de Operação</li>
              <li>Delimitação de Responsabilidades</li>
              <li>Objeto e Acesso ao Serviço</li>
              <li>Planos, Pagamento e Cancelamento</li>
              <li>Política de Suporte Técnico</li>
              <li>Obrigações e Responsabilidades</li>
              <li>Propriedade Intelectual</li>
              <li>Privacidade e Proteção de Dados (LGPD)</li>
              <li>Disposições Gerais</li>
              <li>Foro e Legislação Aplicável</li>
            </ol>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">1. CONCEITOS IMPORTANTES</h2>
            <p className="text-sm leading-relaxed mb-4">
              Para facilitar a leitura e interpretação deste documento, adotamos as seguintes definições:
            </p>
            <ul className="space-y-3 text-sm">
              <li><strong>Cliente (ou Assinante):</strong> Pessoa física ou jurídica que contrata o acesso à Plataforma Zavvi, responsável pelo pagamento e pela gestão dos Usuários.</li>
              <li><strong>Plataforma Zavvi:</strong> Software desenvolvido e de propriedade exclusiva da ZAVVI, fornecido na modalidade SaaS (Software as a Service). Trata-se de uma solução tecnológica para centralização e gestão de multicanais de atendimento e automação via WhatsApp e Instagram.</li>
              <li><strong>Assinatura:</strong> Modalidade de contratação que concede ao Cliente o direito de acesso e uso da Plataforma Zavvi pelo período contratado.</li>
              <li><strong>Usuário:</strong> Pessoa autorizada pelo Cliente a acessar o painel do sistema (administradores, supervisores ou atendentes).</li>
              <li><strong>Conexão:</strong> Número de WhatsApp ou conta de Instagram conectada à plataforma para gestão de atendimentos.</li>
            </ul>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">2. NATUREZA E EFICÁCIA DOS TERMOS</h2>
            <p className="text-sm leading-relaxed mb-3">
              2.1. Ao contratar o acesso à Plataforma, o Cliente concorda integralmente com estes Termos. A aceitação destas regras é condição indispensável para a liberação do acesso e utilização da Plataforma Zavvi.
            </p>
            <p className="text-sm leading-relaxed mb-3">
              2.2. A realização do pagamento ou o simples início da utilização da Plataforma Zavvi implica, para todos os fins de direito, na aceitação plena, inequívoca e irrevogável de todas as condições estabelecidas nestes Termos e suas futuras atualizações.
            </p>
            <p className="text-sm leading-relaxed mb-3">
              2.3. O Cliente reconhece que estes Termos possuem força de contrato vinculante, substituindo quaisquer acordos verbais ou trocas de mensagens anteriores.
            </p>
            <p className="text-sm leading-relaxed">
              2.4. A ZAVVI poderá alterar estes Termos a qualquer momento. O uso continuado do sistema após a publicação das alterações confirma a aceitação dos novos Termos.
            </p>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">3. REQUISITOS TÉCNICOS E CONDIÇÕES DE OPERAÇÃO</h2>
            <p className="text-sm leading-relaxed mb-4">
              Para garantir a performance, segurança e estabilidade da Plataforma Zavvi, o Cliente deve observar os seguintes requisitos:
            </p>
            
            <h3 className="text-lg font-semibold text-foreground mb-2">3.1. Requisitos do Usuário:</h3>
            <ul className="list-disc list-inside space-y-1 text-sm mb-4">
              <li>Navegador atualizado (Chrome, Firefox, Safari ou Edge)</li>
              <li>Conexão de internet estável</li>
              <li>Dispositivo com capacidade de processamento adequada</li>
            </ul>

            <h3 className="text-lg font-semibold text-foreground mb-2">3.2. Protocolos de Conexão:</h3>
            <p className="text-sm leading-relaxed mb-2">
              <strong>3.2.1. API Oficial do WhatsApp (Plano Elite):</strong> A ZAVVI oferece integração com a API Oficial do WhatsApp (WABA), garantindo maior estabilidade, segurança e suporte direto da Meta.
            </p>
            <p className="text-sm leading-relaxed mb-2">
              <strong>3.2.2. Conexão via QR Code (Plano Light):</strong> Para maior agilidade, o sistema permite conexão via QR Code. O Cliente deve estar ciente dos riscos associados, incluindo possíveis desconexões e limitações impostas pela Meta.
            </p>
            <p className="text-sm leading-relaxed">
              <strong>3.2.3. Veracidade das Informações:</strong> O Usuário deve fornecer informações verdadeiras, exatas e atuais. A ZAVVI reserva o direito de recusar o cadastro ou cancelar contas cujos Usuários adotem condutas contrárias a estes Termos.
            </p>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">4. DELIMITAÇÃO DE RESPONSABILIDADES</h2>
            <p className="text-sm leading-relaxed mb-3">
              4.1. Estes Termos disciplinam exclusivamente a relação comercial entre a ZAVVI e o Cliente (Assinante).
            </p>
            <p className="text-sm leading-relaxed mb-3">
              4.2. <strong>Responsabilidade sobre Equipe:</strong> O Cliente é o único responsável pela seleção, contratação, remuneração e gestão de seus funcionários (Usuários/Atendentes) que utilizam a plataforma, isentando a ZAVVI de qualquer vínculo trabalhista ou solidariedade.
            </p>
            <p className="text-sm leading-relaxed">
              4.3. <strong>Responsabilidade sobre Terceiros:</strong> A ZAVVI atua como fornecedora de tecnologia e não possui relação com os clientes finais do Cliente. O Cliente é o único responsável pelo suporte, cobrança e cumprimento legal perante seus próprios clientes.
            </p>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">5. OBJETO E ACESSO AO SERVIÇO</h2>
            <p className="text-sm leading-relaxed mb-3">
              <strong>5.1. Objeto:</strong> O contrato concede ao Cliente o direito de acesso à Plataforma Zavvi na modalidade SaaS (Software as a Service), de forma não exclusiva, intransferível e temporária.
            </p>
            <p className="text-sm leading-relaxed mb-3">
              <strong>5.2. Escopo:</strong> A assinatura permite acesso à plataforma, cadastro ilimitado de usuários e conexões, uso de automações com IA e recebimento de atualizações durante a vigência contratual.
            </p>
            <p className="text-sm leading-relaxed mb-3">
              <strong>5.3. Restrições:</strong> Sob pena de cancelamento imediato e medidas judiciais, é vedado:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Copiar, vender ou distribuir o software ou seus componentes</li>
              <li>Realizar engenharia reversa ou descompilação do código</li>
              <li>Compartilhar ou transferir o acesso para terceiros não autorizados</li>
              <li>Utilizar a plataforma para fins ilegais ou em desacordo com os termos da Meta/WhatsApp</li>
            </ul>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">6. PLANOS, PAGAMENTO E CANCELAMENTO</h2>
            
            <h3 className="text-lg font-semibold text-foreground mb-2">6.1. Planos Disponíveis:</h3>
            <ul className="list-disc list-inside space-y-1 text-sm mb-4">
              <li><strong>Plano Light:</strong> Conexão via QR Code, ideal para quem busca agilidade</li>
              <li><strong>Plano Elite:</strong> Conexão via API Oficial Meta, ideal para empresas que precisam de máxima segurança contra banimentos</li>
            </ul>

            <h3 className="text-lg font-semibold text-foreground mb-2">6.2. Faturamento:</h3>
            <p className="text-sm leading-relaxed mb-4">
              Os planos são cobrados com faturamento anual. O Cliente declara estar ciente de que o pagamento parcelado não constitui assinatura mensal cancelável.
            </p>

            <h3 className="text-lg font-semibold text-foreground mb-2">6.3. Política de Cancelamento e Reembolso:</h3>
            <p className="text-sm leading-relaxed mb-2">
              <strong>6.3.1. Prazo de Arrependimento (7 Dias):</strong> Em conformidade com o Art. 49 do Código de Defesa do Consumidor, o Cliente poderá solicitar o cancelamento e o reembolso integral do valor pago em até 07 (sete) dias corridos após a data da compra inicial.
            </p>
            <p className="text-sm leading-relaxed">
              <strong>6.3.2. Após 7 Dias:</strong> Decorrido o prazo de 7 dias, o serviço é considerado plenamente entregue e consumado. Não haverá reembolso ou estorno.
            </p>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">7. POLÍTICA DE SUPORTE TÉCNICO</h2>
            
            <h3 className="text-lg font-semibold text-foreground mb-2">7.1. Canais e Horários:</h3>
            <p className="text-sm leading-relaxed mb-4">
              O suporte técnico é realizado via WhatsApp e ticket de suporte, de Segunda a Sexta-feira, das 08h às 18h (Horário de Brasília), exceto feriados nacionais.
            </p>

            <h3 className="text-lg font-semibold text-foreground mb-2">7.2. Escopo do Suporte:</h3>
            <ul className="list-disc list-inside space-y-1 text-sm mb-4">
              <li>Esclarecimento de dúvidas sobre funcionamento da plataforma</li>
              <li>Investigação e correção de bugs ou falhas no sistema</li>
              <li>Auxílio em configurações da plataforma</li>
            </ul>

            <h3 className="text-lg font-semibold text-foreground mb-2">7.3. Limitações:</h3>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Criação de fluxos de chatbot personalizados</li>
              <li>Consultoria de negócios ou marketing</li>
              <li>Suporte para integrações de terceiros não homologadas</li>
            </ul>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">8. OBRIGAÇÕES E RESPONSABILIDADES</h2>
            <p className="text-sm leading-relaxed mb-3">
              <strong>8.1. Obrigações da ZAVVI:</strong> Disponibilizar a plataforma, realizar atualizações de correção/segurança e prestar suporte técnico conforme escopo definido.
            </p>
            <p className="text-sm leading-relaxed mb-3">
              <strong>8.2. Obrigações do Cliente:</strong> Realizar pagamentos em dia, utilizar a plataforma conforme os termos de uso da Meta/WhatsApp, e administrar adequadamente seus usuários.
            </p>
            <p className="text-sm leading-relaxed">
              <strong>8.3. Limitações sobre Terceiros:</strong> A ZAVVI não será responsabilizada por indisponibilidade, mudanças de política, bloqueios ou banimentos aplicados pela Meta, WhatsApp ou terceiros.
            </p>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">9. PROPRIEDADE INTELECTUAL</h2>
            <p className="text-sm leading-relaxed mb-3">
              <strong>9.1. Titularidade da ZAVVI:</strong> A Plataforma Zavvi, seu código-fonte, arquitetura, logotipos, documentações e APIs são de propriedade intelectual exclusiva da ZAVVI.
            </p>
            <p className="text-sm leading-relaxed">
              <strong>9.2. Titularidade do Cliente:</strong> Pertencem exclusivamente ao Cliente: sua base de dados de contatos, históricos de conversas e informações de seus clientes.
            </p>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">10. PRIVACIDADE E PROTEÇÃO DE DADOS (LGPD)</h2>
            <p className="text-sm leading-relaxed mb-3">
              <strong>10.1. ZAVVI como Controladora:</strong> A ZAVVI atua como Controladora apenas dos dados cadastrais do Cliente (nome, e-mail, CNPJ, telefone), utilizados para faturamento e suporte.
            </p>
            <p className="text-sm leading-relaxed mb-3">
              <strong>10.2. Cliente como Controlador:</strong> O Cliente é o único e exclusivo Controlador dos dados pessoais de terceiros (seus clientes finais, leads e contatos) que trafegam pela Plataforma Zavvi.
            </p>
            <p className="text-sm leading-relaxed">
              <strong>10.3.</strong> Para mais informações sobre como tratamos seus dados, consulte nossa <Link to="/privacidade" className="text-cyber-cyan hover:underline">Política de Privacidade</Link>.
            </p>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">11. DISPOSIÇÕES GERAIS</h2>
            <p className="text-sm leading-relaxed mb-3">
              11.1. A ZAVVI poderá, a qualquer momento, modificar/atualizar estes Termos. Caso isso aconteça, o Cliente será notificado e a versão atualizada valerá a partir de sua publicação.
            </p>
            <p className="text-sm leading-relaxed mb-3">
              11.2. <strong>Comunicações:</strong> Todas as comunicações serão consideradas válidas quando enviadas para os endereços de e-mail cadastrados pelo Cliente.
            </p>
            <p className="text-sm leading-relaxed">
              11.3. <strong>Conduta e Respeito:</strong> A ZAVVI reserva-se o direito de suspender o suporte técnico ou cancelar a assinatura caso o Cliente trate a equipe de atendimento com agressividade, ofensas ou comportamento inadequado.
            </p>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">12. FORO E LEGISLAÇÃO APLICÁVEL</h2>
            <p className="text-sm leading-relaxed mb-3">
              12.1. Este contrato é regido pelas leis da República Federativa do Brasil, em especial pelo Código Civil e pelas Leis de Propriedade Intelectual e Software.
            </p>
            <p className="text-sm leading-relaxed">
              12.2. Fica eleito o foro da Comarca de São Paulo - SP como o único competente para dirimir quaisquer dúvidas ou litígios oriundos deste contrato, com renúncia expressa a qualquer outro.
            </p>
          </section>

          <hr className="border-border" />

          <section className="bg-muted/50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-3">Canal de Comunicação</h3>
            <p className="text-sm leading-relaxed">
              Para dúvidas sobre estes Termos, entre em contato através do nosso WhatsApp: <a href="https://wa.me/5511936180993" className="text-cyber-cyan hover:underline" target="_blank" rel="noopener noreferrer">+55 11 93618-0993</a> ou e-mail: <a href="mailto:contato@zavvi.ai" className="text-cyber-cyan hover:underline">contato@zavvi.ai</a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-border bg-card/50">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground text-sm">
            © 2026 Zavvi.ai. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermosDeUso;
